import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, qs } from '../lib/api';
import { date, dateTime, eur, iban } from '../lib/format';
import { useSession } from '../lib/session';
import { Badge, Empty, ErrorBox, Field, Loading, Modal, PageHead, StatusBadge, Tabs, useAction, useLoad } from '../components/ui';

export function Vendors() {
  const s = useSession();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'list' | 'iban'>('list');
  const [importOpen, setImportOpen] = useState(false);
  const list = useLoad(() => api.get(`/vendors${qs({ companyId: s.companyId, q })}`), [s.companyId, q]);
  const changes = useLoad(() => api.get('/bank-changes'), []);
  const pending = (changes.data ?? []).filter((c: any) => c.status === 'pending');
  return (
    <>
      <PageHead title="Proveedores" subtitle="Maestro de proveedores, cuentas bancarias verificadas y lo que Conforme ha aprendido de cada uno."
        actions={s.can('vendor.edit') && s.companyId && <button onClick={() => setImportOpen(true)}>Importar proveedores (CSV)</button>} />
      <Tabs value={tab} onChange={setTab} items={[{ id: 'list', label: 'Proveedores' }, { id: 'iban', label: <>Cuentas bancarias por verificar {pending.length > 0 && <Badge tone="warn">{pending.length}</Badge>}</> }]} />
      {tab === 'list' && (
        <>
          <input placeholder="Buscar por nombre o NIF…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 360, marginBottom: 12 }} />
          <ErrorBox error={list.error} />
          {list.loading && !list.data ? <Loading /> : (list.data ?? []).length === 0 ? <Empty>Aún no hay proveedores. Se dan de alta solos al aprobar su primera factura.</Empty> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Proveedor</th><th>NIF</th><th>Subcuenta</th><th>Cuenta habitual</th><th>IBAN verificado</th><th className="num">Facturas</th><th className="num">Volumen</th></tr></thead>
              <tbody>{list.data.map((v: any) => (
                <tr key={v.id} className="clickable" onClick={() => nav(`/proveedores/${v.id}`)}>
                  <td><strong>{v.name}</strong>{v.blocked && <> <Badge tone="error">Bloqueado</Badge></>}</td>
                  <td className="mono">{v.nif}</td><td className="mono">{v.ledger_account ?? '—'}</td>
                  <td className="mono">{v.default_account ?? (v.memory?.accounts ? Object.entries(v.memory.accounts).sort((a: any, b: any) => b[1] - a[1])[0]?.[0] : '—')}</td>
                  <td className="mono">{v.iban_verified ? iban(v.iban_verified) : v.pending_bank_changes ? <Badge tone="warn">Pendiente de verificar</Badge> : '—'}</td>
                  <td className="num">{v.invoices}</td><td className="num">{eur(v.volume)}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </>
      )}
      {tab === 'iban' && <BankChanges data={changes.data} reload={changes.reload} />}
      {importOpen && <ImportVendors onClose={() => { setImportOpen(false); list.reload(); }} />}
    </>
  );
}

function BankChanges({ data, reload }: { data: any[] | undefined; reload: () => void }) {
  const s = useSession();
  const [decide, setDecide] = useState<any>(null);
  if (!data) return <Loading />;
  return (
    <>
      <div className="alert info" style={{ marginBottom: 12 }}>Verifica cada cuenta llamando al proveedor a un teléfono que ya conocías (nunca al que aparezca en la factura o en el correo). Hasta entonces, sus facturas no pueden entrar en una remesa de pago.</div>
      {data.length === 0 ? <Empty>No hay cuentas bancarias por verificar.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Proveedor</th><th>IBAN anterior</th><th>IBAN nuevo</th><th>Detectado</th><th>Estado</th><th /></tr></thead>
          <tbody>{data.map((c) => (
            <tr key={c.id}>
              <td><strong>{c.vendor_name}</strong><div className="muted mono">{c.vendor_nif}{c.vendor_phone ? ` · ${c.vendor_phone}` : ''}</div></td>
              <td className="mono">{c.old_iban ? iban(c.old_iban) : '—'}</td><td className="mono"><strong>{iban(c.new_iban)}</strong></td>
              <td>{dateTime(c.created_at)}{c.source_invoice_id && <div><Link to={`/facturas/${c.source_invoice_id}`}>Ver factura</Link></div>}</td>
              <td>{c.status === 'pending' ? <Badge tone="warn">Pendiente</Badge> : c.status === 'verified' ? <Badge tone="ok">Verificado</Badge> : <Badge tone="error">Rechazado</Badge>}
                {c.verification_note && <div className="hint">{c.decided_by_name}: {c.verification_note}</div>}</td>
              <td>{c.status === 'pending' && s.can('vendor.verify_iban') && <button onClick={() => setDecide(c)}>Resolver</button>}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {decide && <DecideIban change={decide} onClose={() => { setDecide(null); reload(); }} />}
    </>
  );
}

function DecideIban({ change, onClose }: { change: any; onClose: () => void }) {
  const [note, setNote] = useState('');
  const { busy, run } = useAction();
  const go = async (decision: 'verified' | 'rejected') => { if (await run(() => api.post(`/bank-changes/${change.id}/decide`, { decision, note }), decision === 'verified' ? 'IBAN verificado' : 'IBAN rechazado')) onClose(); };
  return (
    <Modal title={`Verificar cuenta de ${change.vendor_name}`} onClose={onClose} footer={<>
      <button className="danger" disabled={busy || note.length < 10} onClick={() => go('rejected')}>Rechazar (posible fraude)</button>
      <button className="ok" disabled={busy || note.length < 10} onClick={() => go('verified')}>He verificado la cuenta</button>
    </>}>
      <p>IBAN: <strong className="mono">{iban(change.new_iban)}</strong></p>
      <Field label="¿Cómo lo has verificado?" hint="Obligatorio. Ej.: «Llamada al 96 123 45 67 (teléfono de la ficha desde 2024), confirmado con Marta, de administración»."><textarea value={note} onChange={(e) => setNote(e.target.value)} /></Field>
    </Modal>
  );
}

function ImportVendors({ onClose }: { onClose: () => void }) {
  const s = useSession();
  const [csv, setCsv] = useState('');
  const { busy, run } = useAction();
  return (
    <Modal title="Importar proveedores" onClose={onClose} footer={<button className="primary" disabled={busy || !csv} onClick={async () => { const r = await run(() => api.post(`/companies/${s.companyId}/vendors/import`, { csv })); if (r) { alert(`${r.imported} importados${r.errors.length ? `\n${r.errors.join('\n')}` : ''}`); onClose(); } }}>Importar</button>}>
      <p className="hint">CSV con cabeceras: <code>nif;nombre;subcuenta;cuenta_gasto;email</code>. Puedes exportarlo desde tu programa contable.</p>
      <input type="file" accept=".csv,.txt" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setCsv(await f.text()); }} />
      <textarea value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="nif;nombre;subcuenta" style={{ minHeight: 160 }} />
    </Modal>
  );
}

export function VendorDetail() {
  const { id } = useParams();
  const s = useSession();
  const { busy, run } = useAction();
  const { data: v, error, loading, reload } = useLoad(() => api.get(`/vendors/${id}`), [id]);
  const [edit, setEdit] = useState<any>(null);
  const [newIban, setNewIban] = useState('');
  if (loading && !v) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const mem = v.memory ?? {};
  return (
    <>
      <Link to="/proveedores" className="muted">← Proveedores</Link>
      <PageHead title={v.name} subtitle={<span className="mono">{v.nif} · {v.country}</span>} actions={s.can('vendor.edit') && <>
        <button onClick={() => setEdit({ name: v.name, email: v.email ?? '', phone: v.phone ?? '', ledgerAccount: v.ledger_account ?? '', defaultAccount: v.default_account ?? '', defaultCostCenter: v.default_cost_center ?? '', paymentTermsDays: v.payment_terms_days ?? '' })}>Editar</button>
        <button className={v.blocked ? '' : 'danger'} disabled={busy} onClick={async () => { if (await run(() => api.patch(`/vendors/${id}`, { blocked: !v.blocked }))) reload(); }}>{v.blocked ? 'Desbloquear' : 'Bloquear'}</button>
      </>} />
      <div className="grid3">
        <div className="card">
          <h2>Datos</h2>
          <p>Correo: {v.email ?? '—'}<br />Teléfono: {v.phone ?? '—'}<br />Subcuenta: <span className="mono">{v.ledger_account ?? '—'}</span><br />Plazo de pago: {v.payment_terms_days ? `${v.payment_terms_days} días` : '—'}</p>
        </div>
        <div className="card">
          <h2>Cuenta bancaria</h2>
          {v.iban_verified ? <p><Badge tone="ok">Verificada</Badge><br /><span className="mono">{iban(v.iban_verified)}</span><br /><span className="hint">desde {date(v.iban_verified_at)}</span></p> : <p><Badge tone="warn">Sin cuenta verificada</Badge></p>}
          {s.can('vendor.edit') && <div className="row"><input className="mono" placeholder="Nuevo IBAN" value={newIban} onChange={(e) => setNewIban(e.target.value)} /><button disabled={!newIban || busy} onClick={async () => { if (await run(() => api.post(`/vendors/${id}/bank-change`, { iban: newIban }), 'Cambio registrado: pendiente de verificación por otra persona')) { setNewIban(''); reload(); } }}>Solicitar cambio</button></div>}
        </div>
        <div className="card">
          <h2>Lo aprendido</h2>
          <p className="hint">A partir de {v.stats?.n ?? 0} factura(s) aprobada(s){v.stats?.mean ? ` · importe medio ${eur(v.stats.mean)}` : ''}.</p>
          {mem.accounts ? <p>Cuentas: {Object.entries(mem.accounts).map(([k, n]) => <Badge key={k}>{k} × {String(n)}</Badge>)}</p> : <p className="muted">Todavía nada.</p>}
          {mem.costCenters && <p>Centros de coste: {Object.entries(mem.costCenters).map(([k, n]) => <Badge key={k}>{k} × {String(n)}</Badge>)}</p>}
        </div>
      </div>
      <div className="spacer" />
      <div className="grid2">
        <div className="card">
          <h2>Facturas</h2>
          <table><tbody>{v.invoices.map((i: any) => <tr key={i.id}><td><Link to={`/facturas/${i.id}`}>{i.number ?? 's/n'}</Link></td><td>{date(i.issue_date)}</td><td className="num">{eur(i.total)}</td><td><StatusBadge status={i.status} /></td></tr>)}</tbody></table>
        </div>
        <div className="card">
          <h2>Historial de cuentas bancarias</h2>
          {v.bankChanges.length === 0 ? <p className="muted">Sin cambios.</p> : <table><tbody>{v.bankChanges.map((c: any) => <tr key={c.id}><td className="mono">{iban(c.new_iban)}</td><td>{c.status === 'verified' ? <Badge tone="ok">Verificado</Badge> : c.status === 'pending' ? <Badge tone="warn">Pendiente</Badge> : <Badge tone="error">Rechazado</Badge>}</td><td className="hint">{c.verification_note}</td></tr>)}</tbody></table>}
        </div>
      </div>
      {edit && (
        <Modal title="Editar proveedor" onClose={() => setEdit(null)} footer={<button className="primary" disabled={busy} onClick={async () => {
          const body = { ...edit, paymentTermsDays: edit.paymentTermsDays === '' ? null : Number(edit.paymentTermsDays), email: edit.email || null, phone: edit.phone || null };
          if (await run(() => api.patch(`/vendors/${id}`, body), 'Proveedor actualizado')) { setEdit(null); reload(); }
        }}>Guardar</button>}>
          {(['name', 'email', 'phone', 'ledgerAccount', 'defaultAccount', 'defaultCostCenter', 'paymentTermsDays'] as const).map((k) => (
            <Field key={k} label={({ name: 'Nombre', email: 'Correo', phone: 'Teléfono (para verificar cambios de cuenta)', ledgerAccount: 'Subcuenta de proveedor', defaultAccount: 'Cuenta de gasto por defecto', defaultCostCenter: 'Centro de coste por defecto', paymentTermsDays: 'Plazo de pago (días)' } as any)[k]}>
              <input value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />
            </Field>
          ))}
        </Modal>
      )}
    </>
  );
}
